import { useCallback } from 'react';
import {
  useSetCommentMarkerSyncStatusMutation,
  useSetMarkerSyncStatusMutation,
} from '@frameio/shared/integrations/gql';
import { usePluginFeatures } from '../../usePluginFeatures';

const context = { resolveWithUXP: true } as const;

export type CommentMarkerSyncStatusMigration = 'ENABLED' | 'DISABLED';

export type CommentMarkerSyncStatusMigrationMutationParams = {
  assetId: string;
  /** Host timeline identity: Premiere sequence id or AE composition id; agnostic mutation maps this to GraphQL `documentId`. */
  documentId: string;
  status: CommentMarkerSyncStatusMigration;
  onCompleted?: () => void;
  onError?: (error: Error) => void;
};

/**
 * Migration bridge: marker-sync on/off — legacy `setMarkerSyncStatus` vs shared
 * `setCommentMarkerSyncStatus` per {@link usePluginFeatures}. Remove when legacy
 * Premiere UXP schema support is dropped.
 */
export function useCommentMarkerSyncStatusMigrationMutation() {
  const { canUseAgnosticCommentMarkerSync } = usePluginFeatures();

  const [setMarkerSyncStatus] = useSetMarkerSyncStatusMutation({ context });
  const [setCommentMarkerSyncStatus] = useSetCommentMarkerSyncStatusMutation({
    context,
  });

  const setCommentMarkerSyncStatusForDocument = useCallback(
    ({
      assetId,
      documentId,
      onCompleted,
      onError,
      status,
    }: CommentMarkerSyncStatusMigrationMutationParams) => {
      if (canUseAgnosticCommentMarkerSync) {
        return setCommentMarkerSyncStatus({
          onCompleted,
          onError,
          optimisticResponse: {
            __typename: 'Mutation',
            setCommentMarkerSyncStatus: {
              __typename: 'CommentMarkerSyncStatus',
              assetId,
              documentId,
              status,
            },
          },
          refetchQueries: ['GetCommentMarkerSyncStatus'],
          variables: {
            input: {
              assetId,
              documentId,
              status,
            },
          },
        });
      }

      return setMarkerSyncStatus({
        onCompleted,
        onError,
        optimisticResponse: {
          __typename: 'Mutation',
          setMarkerSyncStatus: {
            __typename: 'MarkerSyncStatus',
            assetId,
            sequenceId: documentId,
            status,
          },
        },
        refetchQueries: ['GetMarkerSyncStatus'],
        variables: {
          input: {
            assetId,
            sequenceId: documentId,
            status,
          },
        },
      });
    },
    [
      setCommentMarkerSyncStatus,
      setMarkerSyncStatus,
      canUseAgnosticCommentMarkerSync,
    ]
  );

  return {
    setCommentMarkerSyncStatusForDocument,
  };
}
